import { useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import OutsideClickHandler from 'react-outside-click-handler';
import { modalComponent } from './map';


// Some UI packages will render the dropdowns or other elements in a way that is technically outside of the modal
// Define selectors that should not trigger modal close
const ignoreSelectors = [
    '.dropdown-menu',
    '.react-select__menu',
    '.react-select__option',
    '[data-cy*="assignTo"]',
    '.dropdown-item',
    '.nav-link',
    '.nav-tabs',
    '[role="tablist"]',
    '.tab-content',
    '[role="tabpanel"]'
];

//@TODO: Reassess if this is necessary post-migration
// Simple Bootstrap 5.3 loader
const loadBootstrap = async () => {
    if (window.bootstrap?.Modal) {
        return Promise.resolve();
    }

    try {
        const bootstrapModule = await import('bootstrap');

        // Ensure Bootstrap is attached to window
        if (!window.bootstrap) {
            window.bootstrap = bootstrapModule.default || bootstrapModule;
        }

        if (!window.bootstrap?.Modal && bootstrapModule.Modal) {
            window.bootstrap = { Modal: bootstrapModule.Modal };
        }
    } catch (error) {
        console.error('❌ Bootstrap import failed:', error);
        throw error;
    }
};

const Modals = ({ className = '' }) => {
    // Redux state
    const { type, data, callBack } = useSelector(state => ({
        type: state.app.modalType,
        data: state.app.modalData,
        callBack: state.app.modalCallBack,
    }));

    // Redux dispatch
    const dispatch = useDispatch();
    const onModalClose = useCallback(() => {
        dispatch({
            type: 'MODAL_REQUEST',
            payload: { type: '', data: {} },
        });
    }, [dispatch]);

    const [isLoading, setIsLoading] = useState(true);
    const [currentModal, setCurrentModal] = useState(null);

    // Centralized cleanup function
    const cleanupModal = useCallback((disposeInstance = true) => {
        const modalElement = document.getElementById('app-modal');
        
        if (disposeInstance && modalElement && window.bootstrap?.Modal) {
            try {
                const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                if (existingModal) {
                    existingModal.dispose();
                }
            } catch (error) {
                console.error('Error disposing modal:', error);
            }
        }
        
        // Clean up DOM state
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('padding-right');
        
        // Reset modal element
        if (modalElement) {
            modalElement.style.display = 'none';
            modalElement.setAttribute('aria-hidden', 'true');
            modalElement.classList.remove('show');
        }
    }, []);

    // Run cleanup if no type or loading
    if (!type || isLoading) {
        cleanupModal();
    }

    // Bootstrap loader effect
    useEffect(() => {
        const loadBootstrapAsync = async () => {
            try {
                await loadBootstrap();
                setIsLoading(false);
            } catch (error) {
                console.error('Failed to load Bootstrap:', error);
                setIsLoading(false);
            }
        };
        
        loadBootstrapAsync();
    }, []);

    // Modal management effects  
    useEffect(() => {
        if (type && type !== '') {
            // If switching from one modal to another, hide current first
            if (currentModal && type !== currentModal.type) {
                if (currentModal?.modal) {
                    try {
                        // Use Bootstrap's hidden event to ensure smooth transition
                        const modalElement = document.getElementById('app-modal');
                        const handleHidden = () => {
                            // Clean up only orphaned backdrops, not all active ones
                            const backdrops = document.querySelectorAll('.modal-backdrop:not(.show)');
                            backdrops.forEach(backdrop => backdrop.remove());
                            
                            // Show new modal immediately after cleanup
                            if (modalElement && window.bootstrap?.Modal) {
                                try {
                                    modalElement.removeAttribute('aria-hidden');
                                    
                                    // Check if there's already a modal instance we can reuse
                                    let modal = window.bootstrap.Modal.getInstance(modalElement);
                                    if (!modal) {
                                        // Only create new instance if none exists
                                        modal = new window.bootstrap.Modal(modalElement, {
                                            backdrop: 'static',
                                            keyboard: false,
                                            focus: true,
                                        });
                                    }
                                    modal.show();
                                    setCurrentModal({ modal, type });
                                } catch (error) {
                                    console.error('Error creating/showing Bootstrap modal:', error);
                                }
                            }
                            
                            // Remove event listener after use
                            modalElement?.removeEventListener('hidden.bs.modal', handleHidden);
                        };
                        
                        modalElement?.addEventListener('hidden.bs.modal', handleHidden);
                        currentModal.modal.hide();
                        setCurrentModal(null);
                    } catch (error) {
                        console.error('Error hiding modal:', error);
                    }
                }
            } else if (!currentModal) {
                // Show modal if none is currently shown
                const modalElement = document.getElementById('app-modal');
                if (modalElement && window.bootstrap?.Modal) {
                    try {
                        modalElement.removeAttribute('aria-hidden');
                        
                        // Check if there's already a modal instance we can reuse
                        let modal = window.bootstrap.Modal.getInstance(modalElement);
                        if (!modal) {
                            // Only create new instance if none exists
                            modal = new window.bootstrap.Modal(modalElement, {
                                backdrop: 'static',
                                keyboard: false,
                                focus: true,
                            });
                        }
                        modal.show();
                        setCurrentModal({ modal, type });
                    } catch (error) {
                        console.error('Error creating/showing Bootstrap modal:', error);
                    }
                }
            }
        } else if (currentModal) {
            // Only hide modal when transitioning from open to closed
            if (currentModal?.modal) {
                try {
                    const modalElement = document.getElementById('app-modal');
                    const handleHidden = () => {
                        // Only clean up when actually closing modal completely
                        setTimeout(() => {
                            const backdrops = document.querySelectorAll('.modal-backdrop');
                            backdrops.forEach(backdrop => backdrop.remove());
                            document.body.classList.remove('modal-open');
                            document.body.style.removeProperty('padding-right');
                        }, 50);
                        
                        modalElement?.removeEventListener('hidden.bs.modal', handleHidden);
                    };
                    
                    modalElement?.addEventListener('hidden.bs.modal', handleHidden);
                    currentModal.modal.hide();
                    setCurrentModal(null);
                } catch (error) {
                    console.error('Error hiding modal:', error);
                }
            }
        }
    }, [type, currentModal]);

    // Cleanup effect for unmounting and state changes
    useEffect(() => {
        // Cleanup when no modal should be shown
        if (!type || isLoading) {
            cleanupModal(true);
        }
        
        // Cleanup on unmount
        return () => cleanupModal(true);
    }, [type, isLoading, cleanupModal]);

    const handleClose = useCallback(() => {
        // Hide modal directly
        if (currentModal?.modal) {
            try {
                const modalElement = document.getElementById('app-modal');
                const handleHidden = () => {
                    // Use centralized cleanup after modal is hidden
                    cleanupModal(false); // Don't dispose, let normal flow handle it
                    modalElement?.removeEventListener('hidden.bs.modal', handleHidden);
                };
                
                modalElement?.addEventListener('hidden.bs.modal', handleHidden);
                currentModal.modal.hide();
                setCurrentModal(null);
            } catch (error) {
                console.error('Error hiding modal:', error);
            }
        }

        // Ensure onModalClose is called safely
        try {
            onModalClose();
        } catch (error) {
            console.error('❌ Error in onModalClose:', error);
        }
    }, [currentModal, onModalClose, cleanupModal]);

    // Smart outside click handler that can work with Bootstrap tabs
    const handleSmartOutsideClick = useCallback((e) => {

        // Check if click is on any element that should be ignored
        const shouldIgnore = ignoreSelectors.some(selector => {
            return e.target?.closest(selector) || e.target?.classList?.contains(selector.replace('.', ''));
        });

        if (shouldIgnore) {
            return;
        }

        // Check if click is inside the modal content (not just the modal backdrop)
        const modalContent = document.querySelector('#app-modal .modal-content');
        if (modalContent?.contains(e.target)) {
            return;
        }

        // For all other cases, proceed with normal close
        handleClose();
    }, [handleClose]);

    const modalProps = { onClose: handleClose, callBack, ...data};

    if (!type || isLoading) {
        return null;
    }

    const Component = modalComponent(type);
    const modalClasses = `modal fade ${className || ''}${type === 'CTA' ? ' cta-modal-wrapper' : ''}`;

    return (
        <div id="app-modal" className={modalClasses} tabIndex="-1" aria-labelledby="modalLabel">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-body p-0">
                        <OutsideClickHandler onOutsideClick={handleSmartOutsideClick}>
                            <Component {...modalProps} />
                        </OutsideClickHandler>
                    </div>
                </div>
            </div>
        </div>
    );
};

Modals.propTypes = {
    className: PropTypes.string,
};

export default Modals;
export { Modals as Modal };

